





































































import { Component, Prop, Vue } from 'vue-property-decorator';
import moment from 'moment-timezone';
import api from '@/api';
import downloadOrdersApi from '@/api/modules/downloadOrders';
import CustomerListMobile from './CustomerListMobile.vue';
import CustomerListDesktop from './CustomerListDesktop.vue';

export interface CustomerData {
  PHONE: string;
  USERNAME: string;
  NOTE: string;
  LAST_ORDER_ID: string;
  LAST_ORDER_DATE: string;
  HAS_WHATSAPP: string; // 'Yes' | 'No' | '-'
  TOTAL_ORDER_AMOUNT: number;
  TOTAL_ORDER_COUNT: number;
  GENDER: string;
  AGE_RANGE: string;
  CREATED_TIME: number;
  OUTLET_CODE: string;
}

@Component({
  components: {
    CustomerListMobile,
    CustomerListDesktop
  }
})
export default class CustomerBasic extends Vue {
  @Prop(Boolean) isMobile!: boolean;

  // Window width for responsive detection
  windowWidth: number = 0;

  // Outlet selection
  currentOutletName: string = 'Select Outlet';
  currentOutletCode: string = '';
  selectedOutlet: any = null;
  outletList: any[] = [];
  outletInfo: any[] = [];
  isOutletListVisible: boolean = false;
  isOutletLoading: boolean = true;
  isLoadingMore: boolean = false;
  showLoadMore: boolean = false;
  hasInitializedOutlets: boolean = false;
  filterOutletText: string = '';
  currentOutletPage: number = 1;
  outletPageSize: number = 100;

  // Export
  isExporting: boolean = false;

  // Search
  searchText: string = '';

  // Pagination
  currentPage = 1;
  perPage = 20;
  perPageOptions = [10, 20, 50, 100];

  // Loading state
  isLoadingCustomers: boolean = false;

  // Customers for selected store
  customers: CustomerData[] = [];

  // Statistics from API
  apiTotalCustomers: number = 0;
  apiTotalOrders: number = 0;
  apiTotalRevenue: number = 0;

  // Computed properties
  get userType() {
    return this.$store.state.currentUser ? this.$store.state.currentUser.Type : '';
  }

  get isAdmin() {
    return this.userType === 'Admin';
  }

  // Tablets (>= 768px) are treated as Desktop
  get isSmallMobile() {
    // Use both prop and window width for detection
    // Tablets (width >= 768px) should use Desktop view
    return this.isMobile && this.windowWidth < 768;
  }

  get displayedOutletList(): any[] {
    if (!this.filterOutletText) {
      return this.outletList;
    }
    const search = this.filterOutletText.toLowerCase();
    return this.outletList.filter(outlet =>
      outlet.text.toLowerCase().includes(search) ||
      outlet.value.toString().toLowerCase().includes(search)
    );
  }

  get filteredItems(): CustomerData[] {
    // Since filtering is now done on the backend, just return all customers
    return this.customers;
  }

  get paginatedItems(): CustomerData[] {
    // Since pagination is now done on the backend, just return all customers
    return this.customers;
  }

  get totalCustomers(): number {
    // Use API count if available, otherwise fallback to filtered list length
    return this.apiTotalCustomers || this.filteredItems.length;
  }

  get totalOrders(): number {
    // Use API count if available, otherwise calculate from customers
    return this.apiTotalOrders || this.customers.reduce((sum, c) => sum + c.TOTAL_ORDER_COUNT, 0);
  }

  get totalRevenue(): number {
    // Use API total if available, otherwise calculate from customers
    return this.apiTotalRevenue || this.customers.reduce((sum, c) => sum + c.TOTAL_ORDER_AMOUNT, 0);
  }

  // Methods
  handleResize() {
    this.windowWidth = window.innerWidth;
  }

  async toggleOutletList() {
    if (!this.isOutletListVisible && !this.hasInitializedOutlets) {
      this.isOutletListVisible = true;
      this.isOutletLoading = true;
      await this.initializeOutlets();
      this.hasInitializedOutlets = true;
    } else {
      this.isOutletListVisible = !this.isOutletListVisible;
    }
  }

  onFilterOutlet(value: string) {
    this.filterOutletText = value;
  }

  async initializeOutlets() {
    const uid = localStorage.getItem('UID');
    const outletCodesString = localStorage.getItem('outletCodes');
    const outletCodes = outletCodesString ? outletCodesString.split(',').filter(c => !!c) : [];

    console.log('[Customer] Initializing outlets...', {
      isAdmin: this.isAdmin,
      outletCodesCount: outletCodes.length,
      uid
    });

    try {
      let outletInfo: any[] = [];

      // First, check if there's a saved outlet selection
      if (localStorage.getItem('selected-outlet')) {
        this.selectedOutlet = JSON.parse(localStorage.getItem('selected-outlet') || '{}');
      }

      if (this.isAdmin || outletCodes.length) {
        let outletList: any[] = [];

        // Always fetch from API to ensure fresh data
        if (this.isAdmin) {
          this.currentOutletPage = 1;
          console.log('[Customer] Fetching outlets for Admin (page 1, size 100)');
          const response = await api.GetAllStoreForNewPort(
            { page: this.currentOutletPage, size: this.outletPageSize },
            { byField: '', isDesc: '' },
            {},
            uid
          );
          outletList = response.data.result.data || [];
          this.showLoadMore = outletList.length >= this.outletPageSize;
          console.log('[Customer] Received outlets:', outletList.length, 'showLoadMore:', this.showLoadMore);
        } else if (outletCodes.length > 0) {
          console.log('[Customer] Fetching outlets for non-admin user:', outletCodes);
          const response = await api.GetAllStoreForNewPort(
            { page: 1, size: outletCodes.length || 100 },
            { byField: '', isDesc: '' },
            {},
            uid
          );
          outletList = response.data.result.data || [];
          this.showLoadMore = false;
          console.log('[Customer] Received outlets:', outletList.length);
        }

        // Format outlet data
        if (outletList && outletList.length > 0) {
          for (let i = 0; i < outletList.length; i++) {
            const element: any = outletList[i];
            outletInfo.push({
              value: element.outlet_code,
              text: `${element.outlet_code} - ${element.name}`,
              isCheck: false,
              ...element
            });
          }
        }

        // If no selected outlet but outletInfo has data, use the first one
        if (!this.selectedOutlet && outletInfo.length) {
          localStorage.setItem('selected-outlet', JSON.stringify(outletInfo[0]));
          this.selectedOutlet = outletInfo[0];
        }

        this.outletInfo = outletInfo;
        this.outletList = [...outletInfo];
        console.log('[Customer] Total outlets loaded:', this.outletList.length);
      }

      // Set current outlet info from selectedOutlet
      if (this.selectedOutlet && this.selectedOutlet.id) {
        localStorage.setItem('pos-order-outlet', JSON.stringify(this.selectedOutlet));
        this.currentOutletName = this.selectedOutlet.text;
        this.currentOutletCode = this.selectedOutlet.value;
      }

      this.isOutletLoading = false;

      // Load customer data for the selected outlet
      if (this.currentOutletCode) {
        await this.loadCustomersForStore(this.currentOutletCode);
      }
    } catch (error) {
      console.error('Failed to load outlets:', error);
      this.isOutletLoading = false;
    }
  }

  async loadMoreOutlets() {
    if (!this.isAdmin || this.isLoadingMore) return;

    this.isLoadingMore = true;
    this.currentOutletPage++;

    try {
      const uid = localStorage.getItem('UID');
      const response = await api.GetAllStoreForNewPort(
        { page: this.currentOutletPage, size: this.outletPageSize },
        { byField: '', isDesc: '' },
        {},
        uid
      );

      const newOutlets = response.data.result.data || [];

      if (newOutlets.length > 0) {
        const formattedOutlets = newOutlets.map((outlet: any) => ({
          value: outlet.outlet_code,
          text: `${outlet.outlet_code} - ${outlet.name}`,
          isCheck: false,
          ...outlet
        }));

        this.outletInfo.push(...formattedOutlets);
        this.outletList.push(...formattedOutlets);
      }

      this.showLoadMore = newOutlets.length >= this.outletPageSize;
      this.isLoadingMore = false;
    } catch (error) {
      console.error('Failed to load more outlets:', error);
      this.isLoadingMore = false;
      this.showLoadMore = false;
    }
  }

  selectOutlet(payload: { outlet: any; index: number }) {
    const { outlet } = payload;
    this.currentOutletName = outlet.text;
    this.currentOutletCode = outlet.value;
    this.selectedOutlet = outlet;
    this.isOutletListVisible = false;
    this.filterOutletText = '';

    // Save to localStorage (match OrderReport behavior)
    if (outlet && outlet.id) {
      localStorage.setItem('pos-order-outlet', JSON.stringify(outlet));
      localStorage.setItem('selected-outlet', JSON.stringify(outlet));
    }

    // Reset to first page when selecting a new outlet
    this.loadCustomersForStore(outlet.value, true);
  }

  async loadCustomersForStore(outletCode: string, resetPage: boolean = false) {
    if (!outletCode) {
      this.customers = [];
      this.apiTotalCustomers = 0;
      this.apiTotalOrders = 0;
      this.apiTotalRevenue = 0;
      this.currentPage = 1;
      return;
    }

    if (resetPage) {
      this.currentPage = 1;
    }

    this.isLoadingCustomers = true;

    try {
      const response = await api.GetCustomerList({
        outlet_code: outletCode,
        keyword: this.searchText,
        page: this.currentPage,
        page_size: this.perPage,
        sort_by: 'rm.created_at',
        sort_order: 'DESC'
      });

      const data = response.data.data;

      console.log('[Customer] API response:', {
        listCount: data.list ? data.list.length : 0,
        totalCount: data.count ? data.count.user_count : 0,
        currentPage: this.currentPage,
        perPage: this.perPage
      });

      // Update statistics
      if (data.count) {
        this.apiTotalCustomers = data.count.user_count || 0;
        this.apiTotalOrders = data.count.order_count || 0;
        this.apiTotalRevenue = data.count.total_sales || 0;
      }

      // Map API response to component data structure
      this.customers = (data.list || []).map((item: any) => ({
        PHONE: item.phone || '',
        USERNAME: item.name || '',
        NOTE: item.note || '',
        LAST_ORDER_ID: item.last_order_id || '',
        LAST_ORDER_DATE: item.last_order_date || '',
        HAS_WHATSAPP: this.mapWhatsAppStatus(item.whatsapp_status),
        TOTAL_ORDER_AMOUNT: item.order_total_sales || 0,
        TOTAL_ORDER_COUNT: item.order_count || 0,
        GENDER: this.mapGender(item.gender),
        AGE_RANGE: item.age_range || '',
        CREATED_TIME: item.created_at ? moment(item.created_at).unix() : 0,
        OUTLET_CODE: outletCode
      }));
    } catch (error) {
      console.error('Failed to load customers:', error);
      this.customers = [];
      this.apiTotalCustomers = 0;
      this.apiTotalOrders = 0;
      this.apiTotalRevenue = 0;
      alert('Failed to load customer data. Please try again.');
    } finally {
      this.isLoadingCustomers = false;
    }
  }

  mapWhatsAppStatus(status: number): string {
    // 2=yes, 1=no, 0=-
    if (status === 2) return 'Yes';
    if (status === 1) return 'No';
    return '-';
  }

  mapGender(gender: string): string {
    const genderMap: { [key: string]: string } = {
      '1': 'Male',
      '2': 'Female',
      '3': 'Other'
    };
    // 0 or unknown returns '-'
    return genderMap[gender] || '-';
  }

  updateSearch(value: string) {
    this.searchText = value;
    // Debounce search to avoid too many API calls
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      if (this.currentOutletCode) {
        // Reset to first page when searching
        this.loadCustomersForStore(this.currentOutletCode, true);
      }
    }, 500);
  }

  searchTimeout: any = null;

  updateCurrentPage(value: number) {
    this.currentPage = value;
    if (this.currentOutletCode) {
      // Don't reset page, just load the new page
      this.loadCustomersForStore(this.currentOutletCode, false);
    }
  }

  updatePerPage(value: number) {
    this.perPage = value;
    if (this.currentOutletCode) {
      // Reset to first page when changing page size
      this.loadCustomersForStore(this.currentOutletCode, true);
    }
  }

  async createExportAuditLog(payload: any) {
    try {
      await downloadOrdersApi.CreateExportAuditLog(payload);
    } catch (err) {
      console.error('CreateExportAuditLog failed', err);
    }
  }

  customerListExportAuditPayload(rowCount: number | null, status = 'success', errorMessage = '') {
    const currentUser = this.$store.state.currentUser || {};
    const filters = {
      outlet_code: this.currentOutletCode,
      outlet_name: this.currentOutletName,
      keyword: this.searchText,
      page: this.currentPage,
      page_size: this.perPage
    };

    return {
      actorLoginId: localStorage.getItem('UID') || '',
      actorEmail: currentUser.Email || '',
      actorName: currentUser.UserName || localStorage.getItem('UserName') || '',
      actorRole: currentUser.Type || '',
      sourceApp: 'legacy-bo',
      exportType: 'customers',
      requestPath: 'customer-list/export-csv',
      outletCodes: this.currentOutletCode,
      filtersJson: JSON.stringify(filters),
      rowCount,
      status,
      errorMessage
    };
  }

  async exportToCSV() {
    if (this.filteredItems.length === 0) {
      await this.createExportAuditLog(this.customerListExportAuditPayload(0, 'failed', 'No data to export'));
      alert('No data to export');
      return;
    }

    this.isExporting = true;

    try {
      const headers = [
        'Phone Number',
        'Username',
        'Note',
        'Last Order ID',
        'Last Order Date',
        'Has WhatsApp',
        'Total Order Amount (IDR)',
        'Total Order Count',
        'Gender',
        'Age Range',
        'Created Time'
      ];

      const rows = this.filteredItems.map(customer => [
        customer.PHONE,
        customer.USERNAME,
        customer.NOTE || '',
        customer.LAST_ORDER_ID || '-',
        customer.LAST_ORDER_DATE,
        customer.HAS_WHATSAPP,
        customer.TOTAL_ORDER_AMOUNT,
        customer.TOTAL_ORDER_COUNT,
        customer.GENDER,
        customer.AGE_RANGE,
        customer.CREATED_TIME
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = moment().format('YYYY-MM-DD_HHmmss');
      const filename = `customers_${this.currentOutletCode}_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await this.createExportAuditLog(this.customerListExportAuditPayload(rows.length));
    } catch (error) {
      console.error('Export error:', error);
      const message = error && error.message ? error.message : 'Failed to export CSV';
      await this.createExportAuditLog(this.customerListExportAuditPayload(null, 'failed', message));
      alert('Failed to export CSV');
    } finally {
      this.isExporting = false;
    }
  }

  async mounted() {
    // Initialize window width
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);

    // Auto-initialize outlets on page load
    await this.initializeOutlets();
    this.hasInitializedOutlets = true;
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);

    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}
